import React, { useState } from 'react';
import { Box, Typography, Paper, Card, CardContent, Chip, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { format } from 'date-fns';

/**
 * Sent & Waiting Tab - Questions sent but not answered yet
 */
const SentWaitingTab = ({ questions }) => {
  const [copySnackbar, setCopySnackbar] = useState({ open: false, message: '' });

  const sentQuestions = questions
    .filter(q => q.status === 'sent')
    .sort((a, b) => {
      const dateA = a.publishDate?.toDate ? a.publishDate.toDate() : new Date(a.publishDate);
      const dateB = b.publishDate?.toDate ? b.publishDate.toDate() : new Date(b.publishDate);
      return dateB - dateA; // Most recent first
    });

  const getDaysSince = (date) => {
    const sentDate = date?.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - sentDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleCopyLink = async (questionId) => {
    const link = `https://ryandymock.com/mom-stories/answer/${questionId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopySnackbar({ open: true, message: 'Link copied to clipboard!' });
    } catch (err) {
      setCopySnackbar({ open: true, message: 'Failed to copy link' });
    }
  };

  return (
    <Box>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: 'warning.light' }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          📨 Sent & Waiting ({sentQuestions.length})
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Questions sent but not answered yet
        </Typography>
      </Paper>

      {/* Sent Questions List */}
      {sentQuestions.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No questions waiting for response
          </Typography>
        </Paper>
      ) : (
        sentQuestions.map((question) => {
          const daysSince = getDaysSince(question.publishDate);
          const sentDate = question.publishDate?.toDate 
            ? question.publishDate.toDate() 
            : new Date(question.publishDate);

          return (
            <Card key={question.id} sx={{ mb: 2, borderRadius: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="h6" gutterBottom>
                      {question.questionText}
                    </Typography>

                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary">
                        📤 Sent: {format(sentDate, 'MMM d, yyyy @ h:mm a')}
                      </Typography>
                      <Chip
                        label={`${daysSince} day${daysSince !== 1 ? 's' : ''} ago`}
                        size="small"
                        color={daysSince > 7 ? 'error' : 'warning'}
                      />
                    </Box>
                  </Box>

                  {/* Copy Link Button */}
                  <Tooltip title="Copy answer link">
                    <IconButton
                      color="primary"
                      onClick={() => handleCopyLink(question.id)}
                      sx={{ ml: 1 }}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Copy Snackbar */}
      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={3000}
        onClose={() => setCopySnackbar({ ...copySnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCopySnackbar({ ...copySnackbar, open: false })}
          severity="success"
          sx={{ width: '100%' }}
        >
          {copySnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SentWaitingTab;
